import { useState, useEffect, useMemo } from 'react';
import { MdSave } from 'react-icons/md';
import { api } from '../utils/services/api';
import { SERVICE } from '../utils/constants/services';
import Swal from 'sweetalert2';
import Select from 'react-select';

export default function NominaModal({ show, setShow, selectedAdvisor, detailData, setDetailData, detailLoading, fecha, sucursalSelect, advisorId, onSave }) {
    const [amountPayroll, setAmountPayroll] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [detailDataLocal, setDetailDataLocal] = useState([]);
    const [payrollId, setPayrollId] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);

    const parseStartDate = (dateValue) => {
        if (!dateValue) return Number.POSITIVE_INFINITY;
        const parsedDate = new Date(dateValue);
        const timeValue = parsedDate.getTime();
        return Number.isNaN(timeValue) ? Number.POSITIVE_INFINITY : timeValue;
    };

    const clientOptions = useMemo(() => {
        const uniqueClients = [...new Set(detailDataLocal
            .map(item => item.cliente)
            .filter(Boolean))];

        return uniqueClients
            .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
            .map((cliente) => ({ value: cliente, label: cliente }));
    }, [detailDataLocal]);

    const filteredAndSortedData = useMemo(() => {
        const filteredData = selectedClient
            ? detailDataLocal.filter(item => item.cliente === selectedClient.value)
            : detailDataLocal;

        return [...filteredData].sort((a, b) => parseStartDate(a.startDate) - parseStartDate(b.startDate));
    }, [detailDataLocal, selectedClient]);

    useEffect(() => {
        if (detailData) {
            setDetailDataLocal(detailData.map(item => ({
                ...item,
                paymentCompleted: item.pagosActualesCompleto || false,
                qtyPaymentsApply: Number(item.pagosActuales !== undefined && item.pagosActuales !== null
                    ? item.pagosActuales
                    : (item.estatusLoan === 'MORATORIO' ? 0 : 1))
            })));
        }
        
        // Cargar monto de nómina actual si existe
        if (selectedAdvisor && show) {
            if (selectedAdvisor.nominaActualId) {
                setPayrollId(selectedAdvisor.nominaActualId);
                setAmountPayroll(selectedAdvisor.nominaActual || '');
            } else {
                setPayrollId(null);
                setAmountPayroll('');
            }
        }

        setSelectedClient(null);
    }, [detailData, show, selectedAdvisor]);

    const handleQtyChange = (rowId, value) => {
        const numericValue = Number.isNaN(Number(value)) ? 0 : Number(value);
        if (numericValue < 0) return;
        setDetailDataLocal(prev => prev.map(item =>
            item.id === rowId ? { ...item, qtyPaymentsApply: numericValue } : item
        ));
    };

    const handleTogglePaymentComplete = (rowId) => {
        setDetailDataLocal(prev => prev.map(item =>
            item.id === rowId ? { ...item, paymentCompleted: !item.paymentCompleted } : item
        ));
    };

    const handleSaveNomina = async () => {
        if (!amountPayroll || parseFloat(amountPayroll) <= 0) {
            Swal.fire('Error', 'Por favor ingrese un monto válido para la nómina', 'warning');
            return;
        }

        setIsSubmitting(true);

        try {
            let currentPayrollId = payrollId;
            
            // 1. Guardar o actualizar nómina principal
            if (payrollId) {
                // Actualizar nómina existente
                const payrollPayload = {
                    Id: payrollId,
                    AdvisorId: advisorId,
                    CentralOfficeId: sucursalSelect.value,
                    Date: fecha,
                    AmountPayroll: parseFloat(amountPayroll)
                };
                
                await api(SERVICE.updatePayrollAdvisors, payrollPayload, 'PUT');
            } else {
                // Crear nueva nómina
                const payrollPayload = {
                    AdvisorId: advisorId,
                    CentralOfficeId: sucursalSelect.value,
                    Date: fecha,
                    AmountPayroll: parseFloat(amountPayroll)
                };

                const payrollResponse = await api(SERVICE.setPayrollAdvisors, payrollPayload, 'POST');

                if (!payrollResponse || !payrollResponse.data) {
                    throw new Error('Error en respuesta de nómina');
                }

                currentPayrollId = payrollResponse.data.data?.id || payrollResponse.data.data;
                setPayrollId(currentPayrollId);
            }

            // 2. Guardar o actualizar detalles de nómina
            for (const item of detailDataLocal) {
                const detailPayload = {
                    PayrollAdvisorsId: currentPayrollId,
                    LoanId: item.loanId,
                    QtyPaymentsApply: item.qtyPaymentsApply ?? 0,
                    PaymentComplete: item.paymentCompleted
                };

                if (item.nominaDetalleId) {
                    // Actualizar detalle existente
                    detailPayload.Id = item.nominaDetalleId;
                    await api(SERVICE.updatePayrollAdvisorsDetails, detailPayload, 'PUT');
                } else {
                    // Crear nuevo detalle
                    await api(SERVICE.setPayrollAdvisorsDetails, detailPayload, 'POST');
                }
            }

            Swal.fire('Éxito', 'Nómina guardada correctamente', 'success');
            setShow(false);
            setAmountPayroll('');
            setDetailDataLocal([]);
            if (onSave) {
                onSave();
            }
        } catch (error) {
            console.log(error);
            Swal.fire('Error', 'No se pudo guardar la nómina', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            style={{
                display: show ? 'flex' : 'none',
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                padding: '20px'
            }}
        >
            <div
                style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '30px',
                    maxWidth: '80vw',
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)'
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <h4 style={{ margin: 0, color: '#333' }}>Nómina - {selectedAdvisor?.aliasAsesor}</h4>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShow(false)}
                        style={{ cursor: 'pointer' }}
                    ></button>
                </div>

                {detailLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Monto Nómina */}
                        <div style={{
                            backgroundColor: '#f0f8ff',
                            border: '2px solid #0d6efd',
                            borderRadius: '10px',
                            padding: '20px',
                            marginBottom: '30px',
                            maxWidth: '600px',
                            margin: '0 auto 30px auto'
                        }}>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#495057',
                                marginBottom: '8px'
                            }}>
                                Monto de Nómina del Asesor
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 240px' }}>
                                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#6c757d' }}>$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="form-control"
                                        style={{
                                            fontSize: '24px',
                                            fontWeight: 'bold',
                                            padding: '10px',
                                            color: '#0d6efd',
                                            textAlign: 'right'
                                        }}
                                        placeholder="0.00"
                                        value={amountPayroll}
                                        onChange={(e) => setAmountPayroll(e.target.value)}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveNomina}
                                    disabled={isSubmitting || !amountPayroll}
                                    style={{ minWidth: '160px', padding: '10px 16px' }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <MdSave style={{ marginRight: '5px' }} />
                                            Guardar Nómina
                                        </>
                                    )}
                                </button>
                            </div>
                            <small style={{ color: '#6c757d', marginTop: '8px', display: 'block', fontSize: '12px' }}>
                                Ingrese el monto total de la nómina para este asesor
                            </small>
                        </div>

                        {/* Tabla de Detalles */}
                        {detailDataLocal.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                                <p>No hay detalles de nómina disponibles</p>
                            </div>
                        ) : (
                            <>
                                <div style={{ marginBottom: '16px', maxWidth: '420px' }}>
                                    <label className='form-label'>Filtrar por cliente:</label>
                                    <Select
                                        isClearable={true}
                                        isSearchable={true}
                                        options={clientOptions}
                                        placeholder='Todos los clientes'
                                        value={selectedClient}
                                        onChange={(option) => setSelectedClient(option)}
                                        noOptionsMessage={() => 'Sin clientes'}
                                    />
                                </div>
                                <div style={{ overflowX: 'auto', marginBottom: '30px' }}>
                                <table className="table table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '90px', minWidth: '90px' }}>Completo</th>
                                            <th style={{ width: '70px', minWidth: '70px' }}>#</th>
                                            <th style={{ minWidth: '120px' }}>Folio</th>
                                            <th style={{ minWidth: '120px' }}>Fecha Inicio</th>
                                            <th style={{ minWidth: '120px' }}>Fecha Fin</th>
                                            <th style={{ minWidth: '200px', maxWidth: '260px' }}>Cliente</th>
                                            <th style={{ width: '120px', minWidth: '120px' }}>Estatus</th>
                                            <th style={{ width: '140px', minWidth: '140px' }}>Pagos a aplicar</th>
                                            <th style={{ width: '150px', minWidth: '150px' }}>Pagos Anteriores</th>
                                            <th style={{ width: '160px', minWidth: '160px' }}>Monto Pagado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAndSortedData.map((item, index) => (
                                            <tr key={item.id}>
                                                <td style={{ 
                                                    textAlign: 'center',
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={item.paymentCompleted}
                                                        onChange={() => handleTogglePaymentComplete(item.id)}
                                                        disabled={isSubmitting}
                                                        style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                                                    />
                                                </td>
                                                <td style={{
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>{index + 1}</td>
                                                <td style={{
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>F-{String(item.loanId).padStart(6, '0')}</td>
                                                <td style={{
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>{item.startDate ? item.startDate.split('T')[0] : '-'}</td>
                                                <td style={{
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>{item.endDate ? item.endDate.split('T')[0] : '-'}</td>
                                                <td style={{ 
                                                    maxWidth: '260px', 
                                                    whiteSpace: 'nowrap', 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis',
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>{item.cliente}</td>
                                                <td style={{
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>
                                                    <span className={`badge ${item.estatusLoan === 'MORATORIO' ? 'bg-danger' : 'bg-success'}`}>
                                                        {item.estatusLoan}
                                                    </span>
                                                </td>
                                                <td style={{
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        className="form-control"
                                                        style={{ width: '90px', fontSize: '12px', padding: '4px' }}
                                                        value={item.qtyPaymentsApply}
                                                        onChange={(e) => handleQtyChange(item.id, e.target.value)}
                                                        disabled={isSubmitting}
                                                    />
                                                </td>
                                                <td style={{
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>{item.pagosAnteriores !== null ? item.pagosAnteriores : '-'}</td>
                                                <td style={{ 
                                                    textAlign: 'right',
                                                    backgroundColor: (item.qtyPaymentsApply > 0) ? '#d4edff' : 'white',
                                                    color: (item.qtyPaymentsApply > 0) ? '#0c5aa0' : 'black'
                                                }}>
                                                    $ {item.montoPagado ? item.montoPagado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                            </>
                        )}

                        {/* Botones de Acción */}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '20px', borderTop: '1px solid #dee2e6' }}>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShow(false)}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
